import './HelpArticles.less';

import React, { memo, useEffect, useMemo } from 'react';
import { dispatch, useReframe } from 'nike-re-framejs';
import PropTypes from 'prop-types';

import EmptyState from 'nike-ui/src/components/empty-state/EmptyState';
import Markdown from 'nike-ui/src/components/markdown/Markdown';
import { useActiveRoute } from 'nike-ui/src/common/routing/NikeRouteContext';
import BlockLoader from '@/ui/BlockLoader';
import { replaceNonAlpaNumeric } from '@/common/client';
import i18n from '@i18n';
import ArticleIcon from '@/icons/ArticleIcon';
import { HELP_ARTICLE, HELP_MENU } from '@/routing/routes';
import { navigate } from '@/routing';
import ArticleBox from '@/article/views/ArticleBox';

/**
 *  Root path component; params: [articleUrl]
 */
const HelpArticles = memo(function HelpArticles({ articleUrl }) {
    const route = useActiveRoute();
    useEffect(() => {
        dispatch(['helpArticles.load']);
    }, []);

    if (route === HELP_ARTICLE) return <HelpArticle articleUrl={articleUrl} />;
    if (route === HELP_MENU) return <HelpMenu />;
});

const HelpMenu = memo(function HelpMenu() {
    const { derefSub } = useReframe();
    const menu = derefSub(['cms.section.menu', 'help']);

    return (
        <div className="help-menu">
            {menu ? (
                menu.entrySeq().map(([title, category]) => (
                    <div className="category" key={title}>
                        <h2 className="category-title">{title}</h2>
                        {category.map((article, index) => (
                            <ArticleBox
                                key={index}
                                route={HELP_ARTICLE}
                                title={article.get('title')}
                                articleUrl={article.get('url')}
                            />
                        ))}
                    </div>
                ))
            ) : (
                <BlockLoader />
            )}
        </div>
    );
});

const HelpArticle = memo(function HelpArticle({ articleUrl }) {
    const canonUrl = useMemo(() => replaceNonAlpaNumeric(articleUrl), [articleUrl]);
    const derefSub = useReframe();
    const article = derefSub(['cms.section.article', 'help', canonUrl]);

    if (!article) {
        const menu = derefSub(['cms.section.menu', 'help']);
        return <EmptyHelpArticle menu={menu} />;
    }

    return <Markdown article>{article.get('text')}</Markdown>;
});

class EmptyHelpArticle extends React.PureComponent {
    static propTypes = {
        menu: PropTypes.any
    };

    goToHelp() {
        navigate(HELP_MENU);
    }

    render() {
        const { menu } = this.props;
        if (!menu) {
            return <BlockLoader />;
        }
        return (
            <EmptyState
                stateIcon={<ArticleIcon width={40} height={40} fill={'#aab2bd'} opacity={1} />}
                buttonClassName="blue-link home-page"
                buttonText={i18n('help.backtohelpmenu')}
                buttonOnClick={this.goToHelp}
            />
        );
    }
}

export default HelpArticles;
